import { useLinkPreview } from '../queries/useLinkPreview.js'
import { hostnameOf } from '../data/linkDetection.js'

/**
 * LinkPreviewCard — renders an OG-style preview card for a URL.
 *
 * Layout mirrors Slack/Discord: vertical accent stripe on the left,
 * hostname + favicon small on top, title bold, description muted,
 * optional thumbnail on the right.
 *
 * While loading we show a slim skeleton so the chat doesn't jump around
 * when the fetch resolves. On error or empty preview we render nothing —
 * the raw link in the message text is still clickable, so the message
 * isn't degraded.
 */
export default function LinkPreviewCard({ url, compact = false }) {
  const { data, isLoading, isError } = useLinkPreview(url)

  if (isError) return null

  if (isLoading) {
    return (
      <div
        style={{
          marginTop: 6,
          padding: '8px 10px',
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          borderLeft: '3px solid #e5e7eb',
          maxWidth: compact ? 240 : 320,
        }}
      >
        <div
          style={{
            height: 10,
            width: '40%',
            background: '#e5e7eb',
            borderRadius: 3,
            marginBottom: 6,
          }}
        />
        <div
          style={{
            height: 12,
            width: '80%',
            background: '#e5e7eb',
            borderRadius: 3,
          }}
        />
      </div>
    )
  }

  if (!data) return null
  const { title, description, image, favicon, siteName } = data
  // Empty preview — don't take up visual space for nothing
  if (!title && !description && !image) return null

  const host = siteName || hostnameOf(url)

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'stretch',
        marginTop: 6,
        padding: '8px 10px',
        background: '#fafafa',
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--accent)',
        borderRadius: 8,
        textDecoration: 'none',
        color: 'inherit',
        maxWidth: compact ? 240 : 320,
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {host && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 10,
              color: 'var(--muted)',
              marginBottom: 3,
              fontWeight: 600,
            }}
          >
            {favicon && (
              <img
                src={favicon}
                alt=""
                width={12}
                height={12}
                style={{ borderRadius: 2 }}
                onError={(e) => (e.currentTarget.style.display = 'none')}
              />
            )}
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {host}
            </span>
          </div>
        )}
        {title && (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--text)',
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
            }}
          >
            {title}
          </div>
        )}
        {description && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--muted)',
              lineHeight: 1.4,
              marginTop: 3,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
            }}
          >
            {description}
          </div>
        )}
      </div>
      {image && (
        <img
          src={image}
          alt=""
          style={{
            width: 60,
            height: 60,
            objectFit: 'cover',
            borderRadius: 6,
            flexShrink: 0,
          }}
          onError={(e) => (e.currentTarget.style.display = 'none')}
        />
      )}
    </a>
  )
}
